import { listPending, resolveOp, drop, markAttempt } from './outbox';
import { clearPending } from './pending';
import { isRetryable, isAuthError } from '../errors';
import { ehConflito } from '../conflitos';

/**
 * Envio da fila.
 *
 * Regras que não são negociáveis:
 *
 * 1. ORDEM. Processa por `seq` e PARA no primeiro que não passou por falta de
 *    conexão. Pular para o próximo quebraria dependência (o serviço precisa
 *    existir antes da sessão de OPU dele) e colocaria save_fiv_session e
 *    save_te_session em paralelo, que travam uma na outra no Postgres.
 *
 * 2. UM DE CADA VEZ. Nada de Promise.all, pelo mesmo motivo.
 *
 * 3. DÁ PARA TENTAR DE NOVO ≠ RECUSA. Sem rede ou com sessão vencida, o item
 *    fica e tenta depois — os dois se resolvem sozinhos (a rede volta, o
 *    usuário entra de novo). Se o servidor recusou de verdade (dado inválido,
 *    permissão negada), tentar de novo daria o mesmo erro para sempre: fica
 *    marcado e precisa de alguém olhando. Nunca é descartado em silêncio.
 */

/** Erro de chave duplicada: o item já tinha sido aplicado numa tentativa anterior. */
const isAlreadyApplied = (err) => err?.code === '23505';

let running = false;
const listeners = new Set();

export const onSyncChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const notify = (state) => listeners.forEach(fn => { try { fn(state); } catch { /* ignora */ } });

/**
 * Tenta enviar tudo o que está na fila para o usuário informado.
 * Devolve { enviados, pendentes, bloqueados }.
 */
export async function flush(userId = null) {
  if (running) return null;
  running = true;
  notify({ syncing: true });

  let sent = 0, blocked = 0, conflitos = 0, needsLogin = false;
  try {
    const pending = await listPending(userId);

    for (const record of pending) {
      const fn = resolveOp(record.op);

      // Operação que sumiu do código (versão antiga do app gravou, versão nova
      // não conhece). Não dá para adivinhar; marca e segue para não travar a fila.
      if (!fn) {
        await markAttempt(record, new Error(`operação desconhecida: ${record.op}`));
        blocked++;
        continue;
      }

      try {
        await fn(...record.args);
        await drop(record.seq);
        if (record.pendingKey) await clearPending(record.pendingKey);
        sent++;
      } catch (err) {
        if (isAlreadyApplied(err)) {
          await drop(record.seq);
          if (record.pendingKey) await clearPending(record.pendingKey);
          sent++;
          continue;
        }
        // Conflito não é falha: é decisão pendente. Fica retido com os dois
        // lados guardados, esperando alguém escolher. Reenviar sobrescreveria
        // o outro em silêncio, e descartar perderia o trabalho de campo.
        if (ehConflito(err)) {
          await markAttempt({
            ...record,
            conflito: { meus: err.meus, servidor: err.servidor, tabela: err.tabela, id: err.id },
          }, err);
          conflitos++;
          continue;
        }

        await markAttempt(record, err);

        // Sem rede ou com sessão vencida, para e mantém a ordem: os dois
        // passam sozinhos depois (a rede volta, o usuário entra de novo). Só
        // conta como bloqueado o que o servidor recusou de verdade.
        if (isRetryable(err)) {
          if (isAuthError(err)) needsLogin = true;
          break;
        }
        blocked++;
      }
    }
  } finally {
    running = false;
    const left = await listPending(userId);
    notify({ syncing: false, pending: left.length, blocked, conflitos, needsLogin });
  }

  const left = await listPending(userId);
  return { sent, pending: left.length, blocked, conflitos, needsLogin };
}
